import React, { useEffect, useState, useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import "../css/Dashboard.css";
import MuiTooltip from '@mui/material/Tooltip';

import ApiClient from "../ApiClient";
import { useDispatch, useSelector } from "react-redux";
import { selectProjects } from "src/stores/projectSlice";
import { useNavigate, useParams, Link } from "react-router-dom";
import moment from "moment";
import ms from "ms";
import { selectUser } from "src/stores/userSlice";
import {
  CalendarDays,
  Clock3,
  Timer,
  UserX,
  CheckCircle2, CheckCircle, FileText, Home, Cake, AlertCircle,
  CalendarRange, ArrowUpRight, Wallet,
  TriangleAlert,
  PartyPopper, CalendarArrowUp,
  Bell, BarChart2, PieChart, UserMinus,
  ArrowRight,
  Briefcase, Clock, Users, X, ChevronRight,
  ChevronDown, Calendar, Check,
  MessageSquare, Settings,
} from "lucide-react";
import {
  EditNotificationAction,
  selectNotification,
} from "src/stores/notificationSlice";
import DashboardSkeleton from "../components/DashboardSkeleton";
import AllProjectsModal from "../components/AllProjectsModal";
// import { colors } from "react-select/dist/declarations/src/theme";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  ChartDataLabels
);

export const Dashboard = () => {
  const [newLeave, setNewLeaves] = useState([]);
  const [newWFH, setNewWFH] = useState([]);
  const [dsrAbsentDate, setDsrAbsentDate] = useState([]);

  const [dashboardData, setDashboardData] = useState({});
  const startMonthEnv = parseInt(process.env.REACT_APP_ORG_YEAR_START_MONTH || 4, 10);
  const startMonthIndex = startMonthEnv - 1;
  const todayDate = new Date();
  const currentYearObj = todayDate.getMonth() >= startMonthIndex ? todayDate.getFullYear() : todayDate.getFullYear() - 1;
  const currentYearStartObj = new Date(currentYearObj, startMonthIndex, 1);
  const nextYearStartObj = new Date(currentYearObj + 1, startMonthIndex, 1);
  const totalProjectHours = dashboardData?.hoursSeries?.reduce((sum, item) => {
    const itemDate = new Date(item.date);
    return (itemDate >= currentYearStartObj && itemDate < nextYearStartObj) ? sum + Number(item.hours || 0) : sum;
  }, 0) || 0;
  const [alerts, setAlerts] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [upcomingFilter, setUpcomingFilter] = useState('All');
  const [myUpcomingLeaves, setMyUpcomingLeaves] = useState([]);
  const [myUpcomingWfh, setMyUpcomingWfh] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const projects = useSelector(selectProjects);
  const navigate = useNavigate();
  const [monthlyHours, setMonthlyHours] = useState(0);
  const [totalHours, setTotalHours] = useState(0);
  const [daysOff, setDaysOff] = useState(0);
  const [dsrAbsent, setDsrAbsent] = useState(0);
  const user = useSelector(selectUser);
  const notification = useSelector(selectNotification);
  const dispatch = useDispatch();
  
  const [barChartData, setBarChartData] = useState(null);
  const [donutData, setDonutData] = useState({ chartData: null, total: 0 });
  const [isAllProjectsModalOpen, setAllProjectsModalOpen] = useState(false);
  const [queriesAnalytics, setQueriesAnalytics] = useState(null);
  const isHR = user?.role === "HR";

  const PRESET_OPTIONS = [
    'Current Month',
    'Previous Month',
    'Last 3 Months',
    'Last 6 Months',
  ];

  const getPresetDates = (presetKey) => {
    const today = moment();
    switch (presetKey) {
      case 'Current Month':
        return {
          startDate: today.clone().startOf('month').format('YYYY-MM-DD'),
          endDate: today.clone().endOf('month').format('YYYY-MM-DD'),
          label: 'Current Month',
        };
      case 'Previous Month':
        return {
          startDate: today.clone().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
          endDate: today.clone().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
          label: 'Previous Month',
        };
      case 'Last 3 Months':
        return {
          startDate: today.clone().subtract(3, 'months').startOf('day').format('YYYY-MM-DD'),
          endDate: today.clone().endOf('day').format('YYYY-MM-DD'),
          label: 'Last 3 Months',
        };
      case 'Last 6 Months':
        return {
          startDate: today.clone().subtract(6, 'months').startOf('day').format('YYYY-MM-DD'),
          endDate: today.clone().endOf('day').format('YYYY-MM-DD'),
          label: 'Last 6 Months',
        };
      default:
        return {
          startDate: today.clone().startOf('month').format('YYYY-MM-DD'),
          endDate: today.clone().endOf('month').format('YYYY-MM-DD'),
          label: 'Current Month',
        };
    }
  };

  const [analyticsPreset, setAnalyticsPreset] = useState('Current Month');
  const [isCustomRange, setIsCustomRange] = useState(false);
  const [analyticsDateRange, setAnalyticsDateRange] = useState(() => getPresetDates('Current Month'));
  const [tempDateRange, setTempDateRange] = useState(() => getPresetDates('Current Month'));
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isAnalyticsLoading, setIsAnalyticsLoading] = useState(false);
  const [exceptionAnalyticsData, setExceptionAnalyticsData] = useState(null);

  const fetchExceptionAnalytics = async (startDate, endDate) => {
    setIsAnalyticsLoading(true);
    try {
      const { data } = await ApiClient.get(
        `${process.env.REACT_APP_BASE_URL}/dashboard/exceptionAnalytics`,
        { params: { startDate, endDate } }
      );
      setExceptionAnalyticsData(data);
    } catch (err) {
      console.error("Failed to fetch exception analytics:", err);
    } finally {
      setIsAnalyticsLoading(false);
    }
  };

  const handleSelectPreset = (preset) => {
    const dates = getPresetDates(preset);
    setAnalyticsPreset(preset);
    setIsCustomRange(false);
    setAnalyticsDateRange(dates);
    setTempDateRange(dates);
    setIsDropdownOpen(false);
    setIsCalendarOpen(false);
    fetchExceptionAnalytics(dates.startDate, dates.endDate);
  };

  const handleApplyCustomDates = () => {
    if (!tempDateRange.startDate || !tempDateRange.endDate) return;
    setIsCustomRange(true);
    setAnalyticsPreset('Custom Range');
    setAnalyticsDateRange(tempDateRange);
    setIsCalendarOpen(false);
    setIsDropdownOpen(false);
    fetchExceptionAnalytics(tempDateRange.startDate, tempDateRange.endDate);
  };

  // const buildWorkHours = (categories, series, id) => {
  // 	var options = {
  // 		series,
  // 		colors: [
  // 			"#fe6383",
  // 			"#36a2eb",
  // 			"#cc65fe",
  // 			"#ffce56",
  // 			"#4cc0c0",
  // 			"#cacbcf",
  // 		],
  // 		//  colors: ['#2E93fA', '#E91E63', '#FF9800','#66DA26', '#546E7A', '#E91E63', '#FF9800'],
  // 		chart: {
  // 			toolbar: {
  // 				show: false,
  // 			},
  // 			height: 300,
  // 			width: "100%",
  // 			type: "area",
  // 			zoom: {
  // 				autoScaleYaxis: true,
  // 			},
  // 		},
  // 		dataLabels: {
  // 			enabled: false,
  // 		},
  // 		toolbar: {
  // 			show: false,
  // 		},
  // 		stroke: {
  // 			curve: "smooth",
  // 			width: 3,
  // 		},
  // 		fill: {
  // 			type: "gradient",
  // 			gradient: {
  // 				shadeIntensity: 1,
  // 				opacityFrom: 0.7,
  // 				opacityTo: 0.9,
  // 				stops: [0, 90, 100],
  // 			},
  // 		},
  // 		grid: {
  // 			show: true,
  // 			borderColor: "#d2d6db",
  // 			strokeDashArray: 2,
  // 			position: "back",
  // 		},
  // 		legend: {
  // 			show: true,
  // 			position: "top",
  // 			horizontalAlign: "right",
  // 		},
  // 		xaxis: {
  // 			type: "datetime",
  // 			// tickAmount: 6,
  // 			min: new Date("01 Apr 2023").getTime(),
  // 			max: new Date().getTime(),
  // 			labels: {
  // 				datetimeFormatter: {
  // 					year: "yyyy",
  // 					month: "MMM 'yy",
  // 					day: "dd MMM",
  // 					hour: "HH:mm",
  // 				},
  // 			},
  // 		},
  // 		yaxis: {
  // 			min: 0,
  // 		},
  // 		itemMargin: {
  // 			horizontal: 0,
  // 			vertical: 0,
  // 		},
  // 		tooltip: {
  // 			//shared: true,
  // 			x: {
  // 				format: "dd MMM yyyy",
  // 			},
  // 		},
  // 	};
  // 	const chart = new ApexCharts(document.querySelector(`#${id}`), options);
  // 	chart.render();
  // }

  const getProjectName = (code) => {
    const project = projects.find((el) => el?.PROJECT_CODE === code);
    return project?.PROJECT_NAME;
  };

  const renderCharts = (data) => {
    let tempseries = {};
    let areaseries = {};
    let series = [];
    let series1 = [];
    if (Object.keys(data).length > 0) {
      const startMonthEnv = parseInt(process.env.REACT_APP_ORG_YEAR_START_MONTH || 4, 10);
      const startMonthIndex = startMonthEnv - 1;
      const currentYearStart = moment()
        .year(currentYearObj)
        .month(startMonthIndex)
        .date(1)
        .startOf("day");

      const rollingThreeMonths = moment().subtract(3, "months");
      // Use whichever is later
      const chartStart = moment.max(currentYearStart, rollingThreeMonths);
      data.barDateRange = `${currentYearStart?.format("MMM D, YYYY")} - ${moment().format("MMM D, YYYY")}`;
      data.radialDateRange = `${chartStart?.format("MMM D, YYYY")} - ${moment().format("MMM D, YYYY")}`;
      data?.hoursSeries?.forEach((item) => {
        const itemDate = moment(item.date);
        
        // Data for Bar Chart: Current Year
        if (itemDate.isSameOrAfter(currentYearStart) && itemDate.isBefore(moment(currentYearStart).add(1, 'years'))) {
          if (!tempseries[item.projectName]) {
            tempseries[item.projectName] = [];
          }
          let time = itemDate.valueOf();
          tempseries[item.projectName].push({ x: time, y: item.hours });
        }

        // Data for Pie Chart
        if (itemDate.isSameOrAfter(chartStart, "day")) {
          if (!areaseries[item.projectName]) {
            areaseries[item.projectName] = [];
          }
          areaseries[item.projectName].push(item.hours);
        }
      });
      const labels = Object.keys(areaseries);
      
      for (const [key, value] of Object.entries(tempseries)) {
        series.push({
          name: key,
          data: value,
        });
      }
      
      for (const [key, value] of Object.entries(areaseries)) {
        const sum = value.reduce(
          (accumulator, currentValue) =>
            parseInt(accumulator) + parseInt(currentValue),
          0
        );
        series1.push(sum);
      }
      
      const pieColors = [
        "#5B8FF9",
        "#F4C95D",
        "#61DDAA",
        "#65789B",
        "#7262FD",
        "#F08BB4",
        "#78D3F8",
        "#9661BC",
        "#F6903D",
        "#008685",
        "#7F84FF",
        "#6DC8EC",
        "#FFB020",
        "#36B37E",
        "#FF5630",
      ];
      const barColors = [
        "#5B8DEF", // Soft Blue
        "#F4C95D", // Warm Amber
        "#9B8AFB", // Soft Violet
        "#F58FA3", // Soft Rose
        "#72C8F5", // Sky Blue
        "#9DD84A", // Fresh Lime
        "#63D2B4", // Mint
        "#B88CF8", // Lavender
        "#F8D78A", // Light Gold
        "#82C88A", // Sage Green
        "#9FB8F8", // Light Indigo
      ];
      
      const totalPieSum = series1.reduce((acc, val) => acc + val, 0);
      const legendData = labels.map((label, i) => ({
        name: label,
        value: series1[i],
        percentage: totalPieSum > 0 ? ((series1[i] / totalPieSum) * 100).toFixed(1) : 0,
        color: pieColors[i % pieColors.length]
      })).sort((a, b) => b.value - a.value);
      
      data.projectLegendData = legendData;
      // setDonutTotal(totalPieSum) removed in favor of single state

      // Prepare Bar Chart Data
      const projectName = series?.map((ele) => ele.name);
      const projectHours = series?.map((ele) =>
        ele?.data
          ?.map((d) => parseInt(d.y))
          ?.reduce((sum = 0, curr) => sum + curr, 0)
      );

      setBarChartData({
        labels: projectName,
        datasets: [
          {
            label: "Total Hours",
            data: projectHours,
            backgroundColor: barColors.slice(0, projectName.length),
            barThickness: 12,
            borderRadius: 6,
            borderSkipped: false
          }
        ]
      });

      // Prepare Donut Chart Data
      setDonutData({
        total: totalPieSum,
        chartData: {
          labels: labels,
          datasets: [
            {
              data: series1,
              backgroundColor: pieColors,
              hoverBackgroundColor: pieColors,
              borderWidth: 2,
              borderColor: '#ffffff',
              hoverBorderColor: '#ffffff',
            }
          ]
        }
      });
    }
  };

  const statistics = () => {
    let modifiedHours = [];
    if (dashboardData?.hoursSeries?.length > 0) {
      modifiedHours = dashboardData.hoursSeries.map((el) => {
        el.projectName = getProjectName(el.project);
        return el;
      });
      dashboardData.hoursSeries = modifiedHours;
    }
    if (dashboardData?.sumOfDayOff?.length > 0) {
      let dayoff = dashboardData?.sumOfDayOff[0]?.count;
      setDaysOff(dayoff ? dayoff : 0);
    }
    if (dashboardData?.sumOfDsrAbsent?.length > 0) {
      let dsrAbsentdays = dashboardData?.sumOfDsrAbsent[0]?.t_absent;
      setDsrAbsent(dsrAbsentdays);
    }
    if (dashboardData?.sumOfHoursWorked?.length > 0) {
      let m_hours = dashboardData?.sumOfHoursWorked[0]?.cm_hours;
      let t_hours = dashboardData?.sumOfHoursWorked[0]?.t_hours;
      setMonthlyHours(m_hours);
      setTotalHours(t_hours);
    }
    renderCharts(dashboardData);
    setDashboardData(prev => ({...prev, _isProcessed: true}));
  };

  const getDashboardData = async () => {
    try {
      const { data } = await ApiClient.get(
        `${process.env.REACT_APP_BASE_URL}/dashboard/EmployeeStats`
      );
      setDashboardData(data);
    } catch (err) {
      console.log(err);
    }
  };
  const getDsrTaskAbsent = async () => {
    const startDate = moment().startOf("month").format("YYYY-MM-DD");
    const alertsAndEvents = await ApiClient.get(
      `${process.env.REACT_APP_BASE_URL}/dashboard/getAlertsAndEvents`
    );
    var Alerts = alertsAndEvents?.data?.filter((ele) => ele.TYPE == "Alert");
    setAlerts(Alerts);
    const data = await ApiClient.get(
      `${process.env.REACT_APP_BASE_URL}/dsr/getAbsentDsr?start_date=${startDate}&end_date=${moment().format("YYYY-MM-DD")}`
    );
    var absentdate = data.data;
    const data2 = await ApiClient.get(
      `${process.env.REACT_APP_BASE_URL}/leave/requestStatus`
    );
    var dates = data2.data;
    const result = [];
    dates.map((ele) => {
      const currentDate = new Date(ele.START_DATE);
      for (let i = 0; i < ele.NO_OF_DAYS; i++) {
        const formattedDate = currentDate.toISOString().slice(0, 10); // Convert to "yyyy-mm-dd" format
        result.push(formattedDate);
        currentDate.setDate(currentDate.getDate() + 1); // Increment the date by 1 day
      }
    });
    var absentFinal = absentdate.filter(
      (ele) => !result.includes(moment(ele.DATE).format("YYYY-MM-DD"))
    );
    setDsrAbsentDate(absentFinal);
  };

  const fetchNewLeaves = async () => {
    try {
      const { data } = await ApiClient.get(
        `${process.env.REACT_APP_BASE_URL}/admin/newLeaveRequest`,
        { params: { mail: user?.mail } }
      );
      setNewLeaves(data);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchNewRequests = async () => {
    try {
      const { data } = await ApiClient.get(
        `${process.env.REACT_APP_BASE_URL}/admin/newWFHRequest`,
        {
          params: {
            mail: user?.mail,
            date: moment(new Date() - ms("15d")).format("YYYY-MM-DD"),
          },
        }
      );
      let tempData = data?.filter((ele) => ele?.STATUS == "Pending");
      setNewWFH(tempData);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchOfficeHolidays = async () => {
    try {
      const { data } = await ApiClient.post(`${process.env.REACT_APP_BASE_URL}/leave/officeholiday`, {
        year: moment().year(),
      });
      const upcomingHolidays = (data || [])
        .filter((el) => el.LOCATION === user?.country || (user?.country !== "India" && el.LOCATION !== "India"))
        .filter((el) => moment(el.DATE).isSameOrAfter(moment(), 'day'))
        .sort((a, b) => new Date(a.DATE) - new Date(b.DATE));
      setHolidays(upcomingHolidays);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchMyUpcomingData = async () => {
    if (!user?.emp_code) return;
    try {
      const [leaveRes, wfhRes] = await Promise.all([
        ApiClient.get(`${process.env.REACT_APP_BASE_URL}/leave/requestStatus`),
        ApiClient.post(`${process.env.REACT_APP_BASE_URL}/leave/prevWFHRequests`, { empcode: user?.emp_code })
      ]);
      const upcomingLeaves = (leaveRes.data || [])
        .filter(el => moment(el.START_DATE).isSameOrAfter(moment(), 'day'));
      const upcomingWfh = (wfhRes.data || [])
        .filter(el => moment(el.START_DATE).isSameOrAfter(moment(), 'day'));
      setMyUpcomingLeaves(upcomingLeaves);
      setMyUpcomingWfh(upcomingWfh);
    } catch (err) {
      console.log(err);
    }
  };

  const combinedUpcoming = useMemo(() => {
    let items = [];
    if (upcomingFilter === 'All' || upcomingFilter === 'Holidays') {
      holidays.forEach(h => items.push({ type: 'Holiday', title: h.OCCASION, dateObj: new Date(h.DATE), startDate: h.DATE, status: 'Holiday' }));
    }
    if (upcomingFilter === 'All' || upcomingFilter === 'Leave') {
      myUpcomingLeaves.forEach(l => items.push({ type: 'Leave', title: l.LEAVE_TYPE || 'Leave', dateObj: new Date(l.START_DATE), startDate: l.START_DATE, endDate: l.END_DATE, status: l.LEAVE_STATUS !== 'Accepted' ? 'Pending' : l.LEAVE_STATUS, days: l.NO_OF_DAYS }));
    }
    if (upcomingFilter === 'All' || upcomingFilter === 'WFH') {
      myUpcomingWfh.forEach(w => items.push({ type: 'WFH', title: 'WFH', dateObj: new Date(w.START_DATE), startDate: w.START_DATE, endDate: w.END_DATE, status: w.STATUS === 'Request' ? 'Pending' : w.STATUS, days: w.NO_OF_DAYS }));
    }
    return items.sort((a, b) => a.dateObj - b.dateObj).slice(0, 10);
  }, [holidays, myUpcomingLeaves, myUpcomingWfh, upcomingFilter]);

  const fetchQueriesAnalytics = async () => {
    try {
      const res = await ApiClient.get(`${process.env.REACT_APP_BASE_URL}/dashboard/employeeQueriesAnalytics`);
      setQueriesAnalytics(res.data);
    } catch (err) {
      console.error("Error fetching queries analytics", err);
    }
  };

  const queriesAgeChartData = useMemo(() => {
    const zeroToSeven = queriesAnalytics?.ageBreakdown?.zeroToSeven || 0;
    const eightToThirty = queriesAnalytics?.ageBreakdown?.eightToThirty || 0;
    const thirtyPlus = queriesAnalytics?.ageBreakdown?.thirtyPlus || 0;
    const hasData = zeroToSeven + eightToThirty + thirtyPlus > 0;

    return {
      labels: ['0 - 7 days', '8 - 30 days', '30+ days'],
      datasets: [
        {
          data: hasData ? [zeroToSeven, eightToThirty, thirtyPlus] : [1],
          backgroundColor: hasData ? ['#10b981', '#fbbf24', '#f43f5e'] : ['#e2e8f0'],
          borderWidth: 0,
          hoverOffset: 4,
          cutout: '72%',
        },
      ],
    };
  }, [queriesAnalytics]);

  const queriesAgeDoughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: (queriesAnalytics?.totalOpen || 0) > 0,
        backgroundColor: '#0f172a',
        titleFont: { size: 11, family: "'Inter', sans-serif", weight: '600' },
        bodyFont: { size: 11, family: "'Inter', sans-serif" },
        padding: { top: 6, bottom: 6, left: 8, right: 8 },
        boxWidth: 8,
        boxHeight: 8,
        boxPadding: 4,
        cornerRadius: 6,
        callbacks: {
          title: (items) => items[0]?.label || '',
          label: (context) => ` ${context.raw} ${Number(context.raw) === 1 ? 'query' : 'queries'}`,
        },
      },
      datalabels: { display: false },
    },
  };

  useEffect(() => {
    const fetchAllData = async () => {
      setIsLoading(true);
      try {
        const promises = [
          getDashboardData(),
          getDsrTaskAbsent(),
          fetchOfficeHolidays(),
          fetchMyUpcomingData()
        ];
        if (user?.role == "HR") {
          promises.push(fetchNewLeaves());
          promises.push(fetchNewRequests());
          promises.push(fetchQueriesAnalytics());
        }
        await Promise.all(promises);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      fetchAllData();
    }
  }, [user]);

  useEffect(() => {
    if (!isLoading && dashboardData && Object.keys(dashboardData).length > 0 && !dashboardData._isProcessed) {
      statistics();
    }
  }, [dashboardData, isLoading]);

const handleAlertcontainers = () => {
  setTimeout(() => {
    const el = document.getElementById("alertCartToShow");
    if (!el) return;

    el.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });

    el.style.scrollMarginTop = "100px";
    el.style.transition =
      "box-shadow 250ms ease, transform 250ms ease";

    // Thin modern highlight
    el.style.boxShadow = "0 0 0 2px rgba(59, 130, 246, 0.45)";
    el.style.transform = "scale(1.01)";

    setTimeout(() => {
      el.style.boxShadow = "";
      el.style.transform = "";
    }, 1800);
  }, 100);
};
  var param = useParams();
  useEffect(() => {
    if (param.component == "alerts" && !isLoading) {
      handleAlertcontainers();
      if (alerts.length > 0 && user) {
        var unread = alerts.filter(
          (ele) => !ele?.IS_READ?.includes(user.emp_code)
        );
        if (unread.length > 0) {
          markAsRead();
        }
      }
    }
  }, [param, alerts, user, isLoading]);

  const markAsRead = async () => {
    var unReadAlerts = alerts.filter(
      (ele) => !ele?.IS_READ?.includes(user.emp_code)
    );
    try {
      await ApiClient.post(
        `${process.env.REACT_APP_BASE_URL}/dashboard/readAlertsOrEvents`,
        { Unread: unReadAlerts, empcode: user.emp_code }
      );
      var data = { ...notification, ["Alerts"]: "No" };
      dispatch(EditNotificationAction(data));
    } catch (error) {
      console.log(error);
    }
  };

  const barOptions = {
    indexAxis: 'y',
    maintainAspectRatio: false,
    responsive: true,
    layout: {
      padding: { right: 40 }
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#000',
        titleFont: { size: 12 },
        bodyFont: { size: 13 },
        callbacks: {
          label: function(context) { return context.raw + ' Hours'; }
        }
      },
      datalabels: {
        anchor: 'end',
        align: 'right',
        color: '#334155',
        font: { size: 12, family: "'Inter', sans-serif" },
        formatter: function(value) { return value + ' hrs'; }
      }
    },
    scales: {
      x: {
        grid: { display: false, drawBorder: false },
        ticks: { font: { size: 12, family: "'Inter', sans-serif" }, color: '#64748b' },
        title: { display: true, text: 'Hours', font: { size: 13, weight: 'bold', family: "'Inter', sans-serif" }, color: '#334155' }
      },
      y: {
        grid: { display: false, drawBorder: false },
        ticks: {
          font: { size: 12, weight: 500, family: "'Inter', sans-serif" },
          color: '#475569',
          callback: function(value, index, ticks) {
            const label = this.getLabelForValue(value);
            if (!label) return label;
            const isMobile = window.innerWidth < 768;
            const limit = isMobile ? 12 : 25;
            return label.length > limit ? label.substr(0, limit) + '...' : label;
          }
        },
        title: { display: true, text: 'Projects', font: { size: 13, weight: 'bold', family: "'Inter', sans-serif" }, color: '#475569', padding: { bottom: 15 } }
      }
    }
  };

  const donutOptions = {
    maintainAspectRatio: false,
    responsive: true,
    cutout: '70%',
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#000',
        titleFont: { size: 12, family: "'Inter', sans-serif" },
        bodyFont: { size: 13, family: "'Inter', sans-serif" },
        callbacks: {
          title: function(tooltipItems) {
            const label = tooltipItems[0].label || '';
            const words = label.split(' ');
            let lines = [];
            let currentLine = '';
            words.forEach(word => {
              if ((currentLine + word).length > 22) {
                if (currentLine) lines.push(currentLine.trim());
                currentLine = word + ' ';
              } else {
                currentLine += word + ' ';
              }
            });
            if (currentLine) lines.push(currentLine.trim());
            return lines;
          },
          label: function(context) { return context.raw + ' Hours'; }
        }
      },
      datalabels: { display: false }
    }
  };

  const isDsrException = dashboardData?.dsrException?.[0]?.DSR_EXCEPTION === "Yes";

  const activeExceptionStats = exceptionAnalyticsData || dashboardData?.exceptionStats;

  const exceptionProjectDistribution = useMemo(() => {
    return activeExceptionStats?.projectDistribution || [];
  }, [activeExceptionStats]);

  const exceptionPieData = useMemo(() => {
    if (!exceptionProjectDistribution || exceptionProjectDistribution.length === 0) return null;

    let finalLabels = [];
    let finalData = [];

    if (exceptionProjectDistribution.length > 7) {
      finalLabels = exceptionProjectDistribution.slice(0, 7).map(p => p.name);
      finalData = exceptionProjectDistribution.slice(0, 7).map(p => p.hours);
      const othersHours = exceptionProjectDistribution.slice(7).reduce((sum, p) => sum + p.hours, 0);
      if (othersHours > 0) {
        finalLabels.push(`Others (${exceptionProjectDistribution.length - 7} Projects)`);
        finalData.push(Number(othersHours.toFixed(1)));
      }
    } else {
      finalLabels = exceptionProjectDistribution.map(p => p.name);
      finalData = exceptionProjectDistribution.map(p => p.hours);
    }

    const pieColors = [
      '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899',
      '#06b6d4', '#f43f5e', '#14b8a6', '#84cc16', '#6366f1',
      '#64748b'
    ];

    const totalFilteredHours = Number(finalData.reduce((acc, val) => acc + Number(val || 0), 0).toFixed(1));

    const legendData = finalLabels.map((label, i) => ({
      name: label,
      value: finalData[i],
      percentage: totalFilteredHours > 0 ? ((finalData[i] / totalFilteredHours) * 100).toFixed(1) : 0,
      color: pieColors[i % pieColors.length],
    }));

    return {
      chartData: {
        labels: finalLabels,
        datasets: [
          {
            data: finalData,
            backgroundColor: pieColors.slice(0, finalLabels.length),
            borderWidth: 0,
          },
        ],
      },
      legendData,
      totalHours: totalFilteredHours,
    };
  }, [exceptionProjectDistribution]);


  const exceptionBarChartData = useMemo(() => {
    const taskTypeDist = activeExceptionStats?.taskTypeDistribution;
    if (!taskTypeDist || taskTypeDist.length === 0) return null;

    return {
      labels: taskTypeDist.map(t => t.taskType),
      datasets: [
        {
          label: 'Hours',
          data: taskTypeDist.map(t => t.hours),
          backgroundColor: '#3b82f6',
          borderRadius: 4,
          barThickness: 12,
        }
      ]
    };
  }, [activeExceptionStats]);

  const exceptionPieOptions = {
    maintainAspectRatio: false,
    responsive: true,
    cutout: '70%',
    devicePixelRatio: typeof window !== 'undefined' && window.devicePixelRatio > 1 ? window.devicePixelRatio : 2,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#000',
        titleFont: { size: 12, family: "'Inter', sans-serif", weight: '600' },
        bodyFont: { size: 13, family: "'Inter', sans-serif" },
        padding: 12,
        callbacks: {
          title: function(context) {
            const title = context[0]?.label || '';
            const chunks = [];
            let current = '';
            const words = title.split(' ');
            words.forEach(word => {
              if ((current + word).length > 30) {
                if (current) chunks.push(current.trim());
                current = word + ' ';
              } else {
                current += word + ' ';
              }
            });
            if (current.trim()) chunks.push(current.trim());
            return chunks;
          },
          label: function(context) {
            return `${context.raw} hrs`;
          }
        }
      },
      datalabels: { display: false }
    }
  };

  const exceptionBarOptions = {
    indexAxis: 'y',
    maintainAspectRatio: false,
    responsive: true,
    devicePixelRatio: typeof window !== 'undefined' && window.devicePixelRatio > 1 ? window.devicePixelRatio : 2,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#000',
        titleFont: { family: "'Inter', sans-serif" },
        bodyFont: { family: "'Inter', sans-serif" },
        callbacks: {
          label: function(context) {
            return `${context.raw} hrs`;
          }
        }
      },
      datalabels: { display: false }
    },
    scales: {
      x: {
        grid: { display: true, drawBorder: false, color: '#f1f5f9' },
        ticks: { font: { family: "'Inter', sans-serif", size: 12, weight: '500' }, color: '#475569' },
        border: { display: false },
        title: { display: true, text: 'Hours', font: { family: "'Inter', sans-serif", size: 11, weight: '600' }, color: '#64748b' }
      },
      y: {
        grid: { display: false, drawBorder: false },
        ticks: {
          font: { family: "'Inter', sans-serif", size: 12, weight: '500' },
          color: '#475569',
          callback: function(value) {
            const label = this.getLabelForValue(value) || '';
            return label.length > 25 ? label.substring(0, 25) + '...' : label;
          }
        },
        border: { display: false },
        title: { display: false }
      }
    }
  };

  if (isLoading) {
    return <DashboardSkeleton />;
  }
  const activeTab = upcomingFilter === 'Leave' ? "1" : upcomingFilter === 'WFH' ? "2" : "3";
  return (
    <div className="bg-transparent w-full h-full flex flex-col overflow-y-auto overflow-x-hidden">
      <div className="max-w-[1700px] mx-auto px-2 sm:px-4 py-2 sm:py-2 flex-1 w-full flex flex-col mb-2">
        
        {/* Top Row: Metrics */}
        <div className="flex flex-col mb-2">
          
          {/* Row 1: KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-3">
          
            {/* If DSR Exception is Yes: Render Executive Analytics Chips */}
            {isDsrException ? (
              <>
                {/* Total Project Hours (Org-wide) */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 flex items-center gap-3 min-h-[70px]">
                  <div className="w-10 h-10 rounded-2xl bg-indigo-50 flex items-center justify-center shrink-0">
                    <Clock className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div className="flex-1 min-w-0 flex flex-col justify-center">
                    <p className="text-[10px] sm:text-xs font-medium text-slate-500 truncate mb-0.5" title="Project Hours">Project Hours</p>
                    <h2 className="text-xl lg:text-2xl font-bold text-slate-900 leading-none truncate">
                      {dashboardData?.exceptionStats?.totalProjectHours?.toLocaleString() || 0}{" "}
                      <span className="text-xs sm:text-[13px] text-slate-400 font-medium">hrs</span>
                    </h2>
                    <p className="text-[10px] font-medium text-slate-400 mt-0.5 truncate">Current Month</p>
                  </div>
                </div>

                {/* Employees on Leave (Current Month) */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 flex items-center gap-3 min-h-[70px]">
                  <div className="w-10 h-10 rounded-2xl bg-amber-50 flex items-center justify-center shrink-0">
                    <UserMinus className="w-5 h-5 text-amber-600" />
                  </div>
                  <div className="flex-1 min-w-0 flex flex-col justify-center">
                    <p className="text-[10px] sm:text-xs font-medium text-slate-500 truncate mb-0.5" title="On Leave">On Leave</p>
                    <h2 className="text-xl lg:text-2xl font-bold text-slate-900 leading-none truncate">
                      {dashboardData?.exceptionStats?.employeesOnLeaveCurrentMonth || 0}
                    </h2>
                    <p className="text-[10px] font-medium text-slate-400 mt-0.5 truncate">Current Month</p>
                  </div>
                </div>

                {/* Days Off (Personal) */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 flex items-center gap-3 min-h-[70px]">
                  <div className="w-10 h-10 rounded-2xl bg-blue-50 flex items-center justify-center shrink-0">
                    <CalendarDays className="w-4 h-4 sm:w-4.5 sm:h-4.5 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0 flex flex-col justify-center">
                    <p className="text-[10px] sm:text-xs font-medium text-slate-500 truncate mb-0.5" title="Days Off">Days Off</p>
                    <h2 className="text-xl lg:text-2xl font-bold text-slate-900 leading-none truncate">{Number(daysOff || 0)}</h2>
                    <p className="text-[10px] font-medium text-slate-400 mt-0.5 truncate">Current year</p>
                  </div>
                </div>

                {/* Active Projects */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 flex items-center gap-3 min-h-[70px]">
                  <div className="w-10 h-10 rounded-2xl bg-emerald-50 flex items-center justify-center shrink-0">
                    <Briefcase className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div className="flex-1 min-w-0 flex flex-col justify-center">
                    <p className="text-[10px] sm:text-xs font-medium text-slate-500 truncate mb-0.5" title="Active Projects">Active Projects</p>
                    <h2 className="text-xl lg:text-2xl font-bold text-slate-900 leading-none truncate">
                      {dashboardData?.exceptionStats?.activeProjectsCount || 0}
                    </h2>
                    <p className="text-[10px] font-medium text-slate-400 mt-0.5 truncate">Current year</p>
                  </div>
                </div>

                {/* Employees on WFH (Current Month) */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 flex items-center gap-3 min-h-[70px]">
                  <div className="w-10 h-10 rounded-2xl bg-purple-50 flex items-center justify-center shrink-0">
                    <Home className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="flex-1 min-w-0 flex flex-col justify-center">
                    <p className="text-[10px] sm:text-xs font-medium text-slate-500 truncate mb-0.5" title="On WFH">On WFH</p>
                    <h2 className="text-xl lg:text-2xl font-bold text-slate-900 leading-none truncate">
                      {dashboardData?.exceptionStats?.employeesOnWfhCurrentMonth || 0}
                    </h2>
                    <p className="text-[10px] font-medium text-slate-400 mt-0.5 truncate">Current Month</p>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Monthly Hours */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 flex items-center gap-3 min-h-[70px]">
                  <div className="w-10 h-10 rounded-2xl bg-purple-50 flex items-center justify-center shrink-0">
                    <Clock3 className="w-4 h-4 sm:w-4.5 sm:h-4.5 text-purple-600" />
                  </div>
                  <div className="flex-1 min-w-0 flex flex-col justify-center">
                    <p className="text-[10px] sm:text-xs font-medium text-slate-500 truncate mb-0.5">Hours Worked</p>
                    <h2 className="text-xl lg:text-2xl font-bold text-slate-900 leading-none truncate">{Number(monthlyHours || 0)}</h2>
                    <p className="text-[10px] font-medium text-slate-400 mt-0.5 truncate">Current Month</p>
                  </div>
                </div>

                {/* Total Hours */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 flex items-center gap-3 min-h-[70px]">
                  <div className="w-10 h-10 rounded-2xl bg-teal-50 flex items-center justify-center shrink-0">
                    <Timer className="w-5 h-5 text-teal-600" />
                  </div>
                  <div className="flex-1 min-w-0 flex flex-col justify-center">
                    <p className="text-[10px] sm:text-xs font-medium text-slate-500 truncate mb-0.5">Total Hours</p>
                    <h2 className="text-xl lg:text-2xl font-bold text-slate-900 leading-none truncate">{Number(totalHours || 0)}</h2>
                    <p className="text-[10px] font-medium text-slate-400 mt-0.5 truncate">All time</p>
                  </div>
                </div>

                {/* Current Year Project Hours */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 flex items-center gap-3 min-h-[70px]">
                  <div className="w-10 h-10 rounded-2xl bg-indigo-50 flex items-center justify-center shrink-0">
                    <BarChart2 className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div className="flex-1 min-w-0 flex flex-col justify-center">
                    <p className="text-[10px] sm:text-xs font-medium text-slate-500 truncate mb-0.5">Project Hours</p>
                    <h2 className="text-xl lg:text-2xl font-bold text-slate-900 leading-none truncate">{totalProjectHours}</h2>
                    <p className="text-[10px] font-medium text-slate-400 mt-0.5 truncate">Current year</p>
                  </div>
                </div>

                {/* Days Off */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 flex items-center gap-3 min-h-[70px]">
                  <div className="w-10 h-10 rounded-2xl bg-blue-50 flex items-center justify-center shrink-0">
                    <CalendarDays className="w-4 h-4 sm:w-4.5 sm:h-4.5 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0 flex flex-col justify-center">
                    <p className="text-[10px] sm:text-xs font-medium text-slate-500 truncate mb-0.5">Days Off</p>
                    <h2 className="text-xl lg:text-2xl font-bold text-slate-900 leading-none truncate">{Number(daysOff || 0)}</h2>
                    <p className="text-[10px] font-medium text-slate-400 mt-0.5 truncate">Current year</p>
                  </div>
                </div>

                {/* DSR Absent */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 flex items-center gap-3 min-h-[70px]">
                  <div className="w-10 h-10 rounded-2xl bg-red-50 flex items-center justify-center shrink-0">
                    <UserX className="w-5 h-5 text-red-600" />
                  </div>
                  <div className="flex-1 min-w-0 flex flex-col justify-center">
                    <p className="text-[10px] sm:text-xs font-medium text-slate-500 truncate mb-0.5">DSR Absent</p>
                    <h2 className="text-xl lg:text-2xl font-bold text-slate-900 leading-none truncate">{Number(dsrAbsent || 0)}</h2>
                    <p className="text-[10px] font-medium text-slate-400 mt-0.5 truncate">Current Month</p>
                  </div>
                </div>
              </>
            )}

          </div>

          {/* Row 2: Leave Balance, Upcoming Holidays, Alerts */}
          <div className={`grid grid-cols-1 ${user?.role_type !== "GT" && dashboardData?.leave?.[0] ? "lg:grid-cols-3" : "lg:grid-cols-2"} gap-2 sm:gap-3 mt-2 sm:mt-3`}>
            
            {/* Leave Balance */}
            {dashboardData?.leave?.[0] && user?.role_type !== "GT" && (
             <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 sm:p-4 min-h-[250px] flex flex-col">

  {/* Header */}
  <div className="flex items-center justify-between pb-3 border-b border-slate-100 gap-2">
    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
 <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-teal-50 border border-teal-100 shadow-sm flex items-center justify-center shrink-0">
  <CalendarRange className="h-4 w-4 text-teal-600" />
</div>

      <div className="min-w-0">
        <h3 className="text-base sm:text-lg font-semibold text-slate-900 truncate">
          Leave Balance
        </h3>
        <p className="text-[11px] sm:text-sm text-slate-500 truncate hidden xs:block">
          Available leave summary
        </p>
      </div>
    </div>

  </div>

  {/* Statistics */}
  <div className="grid grid-cols-3 gap-1 sm:gap-2 mt-0 flex-1 items-center">

    {/* Current */}
    <div className="text-center flex flex-col items-center gap-2">
      <div className="mx-auto mb-1.5 w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
        <CalendarDays className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600" />
      </div>

      <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-900 truncate w-full">
        {dashboardData.leave[0].CURRENT_YEAR_LEAVE_BALANCE}
      </h2>

      <p className="mt-1 text-[10px] sm:text-xs font-medium text-slate-500 leading-tight">
        Current Year
      </p>
    </div>

    {/* Carry */}
    <div className="text-center border-x border-slate-200 px-1 flex flex-col items-center gap-2">
      <div className="mx-auto mb-1.5 w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
        <CalendarArrowUp className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-600" />
      </div>

      <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-900 truncate w-full">
        {dashboardData.leave[0].PREVIOUS_YEAR_LEAVE_BALANCE}
      </h2>

      <p className="mt-1 text-[10px] sm:text-xs font-medium text-slate-500 leading-tight">
        Carry Forward
      </p>
    </div>

    {/* Total */}
    <div className="text-center flex flex-col items-center gap-2">
      <div className="mx-auto mb-1.5 w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-violet-50 flex items-center justify-center shrink-0">
        <Wallet className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-violet-600" />
      </div>

      <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-900 truncate w-full">
        {dashboardData.leave[0].TOTAL_LEAVE_BALANCE}
      </h2>

      <p className="mt-1 text-[10px] sm:text-xs font-medium text-slate-500 leading-tight">
        Total Balance
      </p>
    </div>

  </div>

</div>
              )}

            {/* Upcoming Section */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 sm:p-4 flex flex-col justify-start relative overflow-hidden group transition-all h-full">
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-slate-50 rounded-full blur-3xl -ml-10 -mb-10 transition-transform group-hover:scale-110"></div>
              
              <div className="relative z-10 flex flex-wrap items-center justify-between mb-4 gap-3 pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2 shrink-0">
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-orange-50 flex items-center justify-center border border-orange-100 shadow-sm shrink-0">
                    <CalendarDays className="h-4 w-4 text-orange-500" />
                  </div>
                  <div className="shrink-0">
                    <h3 className="text-sm sm:text-base font-bold text-slate-800 whitespace-nowrap">Upcoming</h3>
                    <p className="text-[10px] sm:text-[11px] font-medium text-slate-500 truncate hidden xs:block">Your next holidays, leaves & WFH</p>
                  </div>
                </div>
                
                {/* Filter Tabs */}
                {/* <div className="flex gap-2 overflow-x-auto custom-scrollbar-hide max-w-full pb-1">
                  {['All', 'Holidays', 'Leave', 'WFH'].map(tab => (
                    <button
                      key={tab}
                      onClick={() => setUpcomingFilter(tab)}
                      className={`px-3 py-1 rounded-full text-[11px] font-bold whitespace-nowrap transition-colors border ${
                        upcomingFilter === tab 
                          ? 'bg-blue-50 text-blue-600 border-blue-200 shadow-sm' 
                          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      {tab}
                    </button>
                  ))}
                </div> */}
              </div>
              
              <div className="relative z-10 flex-1 flex flex-col min-h-0">
                <div className="overflow-y-auto h-[120px] sm:h-[150px] pr-2 space-y-1.5 custom-scrollbar">
                  {combinedUpcoming && combinedUpcoming.length > 0 ? (
                    combinedUpcoming.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2.5 p-2 sm:p-2.5 bg-white border border-slate-100 rounded-xl shadow-sm hover:border-slate-200 transition-colors">
                        <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center shrink-0 ${
                          item.type === 'Holiday' ? 'bg-orange-50 text-orange-500' :
                          item.type === 'Leave' ? 'bg-purple-50 text-purple-600' :
                          'bg-blue-50 text-blue-500'
                        }`}>
                          {item.type === 'Holiday' && <PartyPopper className="w-4 h-4" />}
                          {item.type === 'Leave' && <UserMinus className="w-4 h-4" />}
                          {item.type === 'WFH' && <Home className="w-4 h-4" />}
                        </div>
                        <div className="min-w-0 flex-1 flex flex-col justify-center">
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-[12px] sm:text-[13px] font-bold text-slate-800 truncate leading-tight mt-0.5">{item.title}</p>
                            {item.type === 'Holiday' ? (
                               <span className="text-[9px] sm:text-[10px] font-bold text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded-md border border-orange-100 shrink-0 whitespace-nowrap">
                                 {(() => {
                                   const days = moment(item.startDate).startOf('day').diff(moment().startOf('day'), 'days');
                                   if (days === 0) return "Today";
                                   if (days === 1) return "1 day remaining";
                                   return `${days} days left`;
                                 })()}
                               </span>
                            ) : (
                               <span className={`text-[9px] sm:text-[10px] font-bold px-1.5 py-0.5 rounded-md border shrink-0 whitespace-nowrap ${
                                  item.status === 'Accepted' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-purple-50 text-purple-600 border-purple-100'
                               }`}>
                                 {item.days} Day{item.days > 1 ? 's' : ''}
                               </span>
                            )}
                          </div>
                          <div className="flex justify-between items-center mt-1">
                             <p className="text-[10px] sm:text-[11px] font-medium text-slate-500 truncate">
                               {item.type === 'Holiday' ? (
                                  moment(item.startDate).format("DD MMM")
                               ) : (
                                  item.endDate && item.startDate !== item.endDate ?
                                  `${moment(item.startDate).format("DD MMM")} - ${moment(item.endDate).format("DD MMM")}` :
                                  moment(item.startDate).format("DD MMM")
                               )}
                             </p>
                             {item.type !== 'Holiday' && (
                                <span className={`text-[9px] sm:text-[10px] font-bold ${item.status === 'Accepted' ? 'text-emerald-500' : 'text-purple-500'}`}>
                                   {item.status}
                                </span>
                             )}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="h-[120px] sm:h-[130px] flex flex-col sm:flex-row items-center justify-between p-4 sm:p-6 bg-slate-50/50 rounded-2xl border border-dashed border-slate-200 my-2 mr-2">
                      <div className="flex items-center gap-3 sm:gap-4">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                          <CalendarDays className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" strokeWidth={2.5} />
                        </div>
                        <div>
                          <p className="text-sm sm:text-base font-bold text-slate-800">Schedule Clear</p>
                          <p className="text-xs sm:text-sm font-medium text-slate-500 mt-0.5">No upcoming items right now.</p>
                        </div>
                      </div>
                      <div className="hidden sm:flex items-center justify-center pr-2 opacity-60">
                        <div className="relative">
                          <CalendarRange className="w-10 h-10 text-blue-400 stroke-[1.5]" />
                          <div className="absolute top-0 right-0 w-3.5 h-3.5 bg-blue-500 rounded-full border-2 border-white flex items-center justify-center">
                            <CheckCircle2 className="w-2 h-2 text-white" strokeWidth={4} />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                {/* <div className="mt-2 pt-2 border-t border-slate-100">
                 {upcomingFilter !== "All" && (
                    <Link
                      to={"/leave"}
                      state={{activeTab}}
                      className="flex items-center gap-1 text-[11px] font-bold text-blue-600 hover:text-blue-700 hover:underline"
                    >
                      View all {upcomingFilter.toLowerCase()}
                      <ArrowRight size={15} />
                    </Link>
                  )}
                </div> */}
              </div>
            </div>

            {/* Alerts */}
            <div id="alertCartToShow" className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 sm:p-4 h-full flex flex-col overflow-hidden relative group transition-all">
              
              <div className="pb-3 mb-3 border-b border-slate-100 flex justify-between items-center bg-transparent shrink-0 relative z-10 gap-2">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-rose-50 flex items-center justify-center border border-rose-100 shadow-sm shrink-0">
                    <Bell className="w-4 h-4 text-rose-500" />
                  </div>
                  <h3 className="text-sm sm:text-base font-bold text-slate-800 truncate flex items-center gap-2">
                    Alerts 
                    {((user?.role === "Admin" ? (newLeave?.length || 0) + (newWFH?.length || 0) : 0) + (dsrAbsentDate?.length || 0) + (alerts?.length || 0)) > 0 && (
                      <span className="bg-rose-100 text-rose-600 px-2 py-0.5 rounded-md text-xs font-bold">
                        {((user?.role === "Admin" ? (newLeave?.length || 0) + (newWFH?.length || 0) : 0) + (dsrAbsentDate?.length || 0) + (alerts?.length || 0))}
                      </span>
                    )}
                  </h3>
                </div>
                {notification.Alerts == "Yes" && (
                  <button onClick={markAsRead} className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-slate-800 transition-colors bg-white px-3 py-1.5 rounded-lg border border-slate-200 shrink-0 whitespace-nowrap shadow-sm hover:bg-slate-50">
                    <CheckCircle className="w-3.5 h-3.5" />
                    Mark All Read
                  </button>
                )}
              </div>
              
              <div className={`flex-1 min-h-0 relative z-10 flex flex-col ${alerts?.length || dsrAbsentDate?.length || newLeave.length || newWFH.length ? "overflow-y-auto custom-scrollbar pr-2 max-h-[160px] sm:max-h-[160px]" : "overflow-hidden"}`}> 
                
                <div className="space-y-2 flex-1 flex flex-col">
                  {user?.role == "Admin" && newLeave?.length > 0 && (
                    <div className="flex items-center gap-3 p-2 sm:p-2.5 bg-white rounded-xl border border-slate-100 border-l-4 border-l-blue-500 shadow-sm cursor-pointer hover:border-blue-200 transition-all" onClick={() => navigate("/super-admin", { state: { tab: '2' } })}>
                      <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                        <FileText className="w-4 h-4 sm:w-4.5 sm:h-4.5 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs sm:text-sm font-bold text-slate-800 truncate">Leave Requests Pending</p>
                        <p className="text-[10px] sm:text-xs font-medium text-slate-500 truncate">{newLeave.length} requests await your approval</p>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="bg-blue-100 text-blue-600 font-bold text-xs px-2 py-0.5 rounded-md">{newLeave.length}</span>
                        {/* <div className="flex items-center gap-1.5">
                          <span className="text-[10px] font-medium text-slate-500">10m ago</span>
                          <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                        </div> */}
                      </div>
                    </div>
                  )}

                  {user?.role == "Admin" && newWFH?.length > 0 && (
                    <div className="flex items-center gap-3 p-2 sm:p-2.5 bg-white rounded-xl border border-slate-100 border-l-4 border-l-purple-500 shadow-sm cursor-pointer hover:border-purple-200 transition-all" onClick={() => navigate("/super-admin", { state: { tab: '3' } })}>
                      <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-purple-50 flex items-center justify-center shrink-0">
                        <Home className="w-4 h-4 sm:w-4.5 sm:h-4.5 text-purple-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs sm:text-sm font-bold text-slate-800 truncate">WFH Requests Pending</p>
                        <p className="text-[10px] sm:text-xs font-medium text-slate-500 truncate">{newWFH.length} requests await your approval</p>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="bg-purple-100 text-purple-600 font-bold text-xs px-2 py-0.5 rounded-md">{newWFH.length}</span>
                        {/* <div className="flex items-center gap-1.5">
                          <span className="text-[10px] font-medium text-slate-500">25m ago</span>
                          <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                        </div> */}
                      </div>
                    </div>
                  )}
                  
                  {dsrAbsentDate?.map((ele, idx) => (
                    <div key={`dsr-${idx}`} className="flex items-center gap-3 p-2 sm:p-2.5 bg-white rounded-xl border border-slate-100 border-l-4 border-l-orange-400 shadow-sm cursor-pointer hover:border-orange-200 transition-all" onClick={() => navigate("/daily-status", { state: { date: ele?.DATE, openModal: true } })}>
                      <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-orange-50 flex items-center justify-center shrink-0">
                        <CalendarDays className="w-4 h-4 sm:w-4.5 sm:h-4.5 text-orange-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs sm:text-sm font-bold text-slate-800 truncate">Daily Status Report Missing</p>
                        <p className="text-[10px] sm:text-xs font-medium text-slate-500 truncate">Please update for {moment(ele.DATE).format("DD MMM YYYY")}</p>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        {/* <span className="bg-orange-100 text-orange-600 font-bold text-xs px-2 py-0.5 rounded-md">1</span> */}
                        {/* <div className="flex items-center gap-1.5">
                          <span className="text-[10px] font-medium text-slate-500">1h ago</span>
                          <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                        </div> */}
                      </div>
                    </div>
                  ))}
                  
                  {alerts?.map((ele, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-2 sm:p-2.5 bg-white rounded-xl border border-slate-100 border-l-4 border-l-rose-500 shadow-sm cursor-pointer hover:border-rose-200 transition-all">
                      <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-rose-50 flex items-center justify-center shrink-0">
                        <AlertCircle className="w-4 h-4 sm:w-4.5 sm:h-4.5 text-rose-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs sm:text-sm font-bold text-slate-800 truncate">Alert</p>
                        <MuiTooltip title={ele.DESCRIPTION} placement="top" arrow>
                          <p className="text-[10px] sm:text-xs font-medium text-slate-500 truncate">{ele.DESCRIPTION}</p>
                        </MuiTooltip>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        {/* <span className="bg-rose-100 text-rose-600 font-bold text-xs px-2 py-0.5 rounded-md">1</span>
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] font-medium text-slate-500">3h ago</span>
                          <span className="w-2 h-2 rounded-full bg-rose-500"></span>
                        </div> */}
                      </div>
                    </div>
                  ))}
                  
                  {alerts?.length === 0 &&
                   dsrAbsentDate?.length === 0 &&
                   (!user || user.role !== "Admin" || (newWFH.length === 0 && newLeave.length === 0)) && (
                    <div className="flex-1 flex flex-col sm:flex-row items-center justify-center sm:justify-between p-6 bg-slate-50/50 rounded-2xl border border-dashed border-slate-200">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                          <CheckCircle2 className="w-8 h-8 text-emerald-600" strokeWidth={2.5} />
                        </div>
                        <div>
                          <p className="text-base font-bold text-slate-800">You're all caught up!</p>
                          <p className="text-sm font-medium text-slate-500 mt-0.5">No new alerts at the moment.</p>
                        </div>
                      </div>
                      <div className="hidden sm:flex items-center justify-center pr-6 opacity-60">
                        <div className="relative">
                          <Bell className="w-12 h-12 text-emerald-400 stroke-[1.5]" />
                          <div className="absolute top-0 right-0 w-4 h-4 bg-emerald-500 rounded-full border-2 border-white flex items-center justify-center">
                            <CheckCircle2 className="w-2 h-2 text-white" strokeWidth={4} />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Charts Row */}
        {isHR ? (
          /* Employee Queries Analytics Section for HR/Admin */
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 mt-2 sm:mt-2">
            {/* Card 1: Employee Queries */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 sm:p-5 flex flex-col justify-between">
              {/* Header */}
              <div className="flex items-center justify-between pb-3.5 mb-4 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 shadow-sm shrink-0">
                    <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5" />
                  </div>
                  <div>
                    <h3 className="text-sm sm:text-base font-bold text-slate-800">
                      Employee Queries
                    </h3>
                    <p className="text-[11px] sm:text-xs text-slate-500 font-medium">Overview of currently active employee queries.</p>
                  </div>
                </div>
                <button
                  onClick={() => navigate("/hr-panel", { state: { activeTab: 'queries' } })}
                  className="flex items-center gap-1.5 text-xs font-bold text-blue-600 hover:text-blue-700 transition-colors hover:underline"
                >
                  View All Queries <ArrowRight size={14} />
                </button>
              </div>

              {/* Status Rows */}
              <div className="space-y-4 flex-1 flex flex-col justify-center py-1">
                {/* Row 1: New Queries */}
                <div className="flex items-center gap-3.5">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold text-slate-800 mb-1.5">New Queries</div>
                    <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 rounded-full transition-all duration-500"
                        style={{
                          width: `${(queriesAnalytics?.totalOpen || 0) > 0 ? (((queriesAnalytics?.pendingCount || 0) / (queriesAnalytics?.totalOpen || 1)) * 100) : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="text-sm font-bold text-slate-800 pl-2 min-w-[24px] text-right">
                    {queriesAnalytics?.pendingCount || 0}
                  </div>
                </div>

                {/* Row 2: In Progress */}
                <div className="flex items-center gap-3.5">
                  <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
                    <Settings className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold text-slate-800 mb-1.5">In Progress</div>
                    <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-purple-400 rounded-full transition-all duration-500"
                        style={{
                          width: `${(queriesAnalytics?.totalOpen || 0) > 0 ? (((queriesAnalytics?.inProgressCount || 0) / (queriesAnalytics?.totalOpen || 1)) * 100) : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="text-sm font-bold text-slate-800 pl-2 min-w-[24px] text-right">
                    {queriesAnalytics?.inProgressCount || 0}
                  </div>
                </div>

                {/* Row 3: Awaiting Employee */}
                <div className="flex items-center gap-3.5">
                  <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                    <Users className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold text-slate-800 mb-1.5">Awaiting Employee</div>
                    <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-amber-400 rounded-full transition-all duration-500"
                        style={{
                          width: `${(queriesAnalytics?.totalOpen || 0) > 0 ? (((queriesAnalytics?.waitingCount || 0) / (queriesAnalytics?.totalOpen || 1)) * 100) : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="text-sm font-bold text-slate-800 pl-2 min-w-[24px] text-right">
                    {queriesAnalytics?.waitingCount || 0}
                  </div>
                </div>
              </div>
            </div>

            {/* Card 2: Query Overview */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 sm:p-5 flex flex-col justify-between">
              {/* Header */}
              <div className="flex items-center gap-3 pb-3.5 mb-4 border-b border-slate-100">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-600 shadow-sm shrink-0">
                  <Briefcase className="w-4 h-4 sm:w-5 sm:h-5" />
                </div>
                <div>
                  <h3 className="text-sm sm:text-base font-bold text-slate-800">
                    Query Overview
                  </h3>
                  <p className="text-[11px] sm:text-xs text-slate-500 font-medium">Distribution of active queries by age.</p>
                </div>
              </div>

              {/* Doughnut Chart & Legend */}
              <div className="flex-1 flex flex-row items-center justify-between min-h-[170px] px-2 sm:px-4">
                {/* Doughnut */}
                <div className="w-[50%] flex items-center justify-center relative">
                  <div className="w-36 h-36 sm:w-40 sm:h-40 relative flex items-center justify-center isolate">
                    <div className="relative z-10 w-full h-full flex items-center justify-center">
                      <Doughnut options={queriesAgeDoughnutOptions} data={queriesAgeChartData} />
                    </div>
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none z-0">
                      <span className="text-2xl sm:text-3xl font-bold text-slate-800 leading-none">
                        {queriesAnalytics?.totalOpen || 0}
                      </span>
                      <span className="text-[11px] text-slate-400 font-semibold mt-1">Active Queries</span>
                    </div>
                  </div>
                </div>

                {/* Legend */}
                <div className="w-[45%] flex flex-col gap-3">
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 shrink-0" />
                      <span className="text-slate-600 font-medium">0 - 7 days</span>
                    </div>
                    <span className="font-bold text-slate-800">{queriesAnalytics?.ageBreakdown?.zeroToSeven || 0}</span>
                  </div>

                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-amber-400 shrink-0" />
                      <span className="text-slate-600 font-medium">8 - 30 days</span>
                    </div>
                    <span className="font-bold text-slate-800">{queriesAnalytics?.ageBreakdown?.eightToThirty || 0}</span>
                  </div>

                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-rose-400 shrink-0" />
                      <span className="text-slate-600 font-medium">30+ days</span>
                    </div>
                    <span className="font-bold text-slate-800">{queriesAnalytics?.ageBreakdown?.thirtyPlus || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : isDsrException ? (
          /* DSR Exception Analytics Section */
          <div className="flex flex-col mt-1 sm:mt-1">
            {/* Section Header Row outside card */}
            <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2 mb-2 sm:mb-2.5">
              <h2 className="text-base sm:text-lg font-bold text-slate-800 tracking-tight ms-2">Project & Task Analytics</h2>

              {/* Date Controls */}
              <div className="flex items-center gap-2 self-start sm:self-auto relative">
                {/* Preset Dropdown Pill */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => {
                      setIsDropdownOpen(!isDropdownOpen);
                      setIsCalendarOpen(false);
                    }}
                    className="flex items-center gap-1.5 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200/90 px-3 py-2.5 rounded-xl text-xs font-semibold transition-all shadow-sm focus:outline-none"
                  >
                    <span className="text-slate-400 font-normal">Period:</span>
                    <span className="font-semibold text-slate-800">
                      {isCustomRange
                        ? `${moment(analyticsDateRange.startDate).format('MMM D, YYYY')} – ${moment(analyticsDateRange.endDate).format('MMM D, YYYY')}`
                        : analyticsPreset}
                    </span>
                    <ChevronDown size={14} className={`text-slate-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Preset Dropdown Menu */}
                  {isDropdownOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setIsDropdownOpen(false)} />
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-slate-200 py-1 z-50 animate-in fade-in zoom-in-95 duration-150">
                        {PRESET_OPTIONS.map((opt) => (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => handleSelectPreset(opt)}
                            className={`w-full flex items-center justify-between px-3.5 py-2 text-xs font-medium text-left transition-colors ${
                              !isCustomRange && analyticsPreset === opt
                                ? 'bg-blue-50 text-blue-700 font-semibold'
                                : 'text-slate-700 hover:bg-slate-50'
                            }`}
                          >
                            <span>{opt}</span>
                            {!isCustomRange && analyticsPreset === opt && <Check size={14} className="text-blue-600" />}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {/* Calendar Button (Custom Range) */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => {
                      setIsCalendarOpen(!isCalendarOpen);
                      setIsDropdownOpen(false);
                      setTempDateRange(analyticsDateRange);
                    }}
                    className={`flex items-center gap-1.5 px-2.5 py-2.5 rounded-xl border text-xs font-medium transition-all shadow-sm ${
                      isCalendarOpen || isCustomRange
                        ? 'bg-blue-50 text-blue-600 border-blue-200 font-semibold'
                        : 'bg-white hover:bg-slate-50 text-slate-600 border-slate-200/90'
                    }`}
                    title="Custom Range"
                  >
                    <Calendar size={14} />
                    <span className="hidden sm:inline">Custom Range</span>
                  </button>

                  {/* Calendar Popover */}
                  {isCalendarOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setIsCalendarOpen(false)} />
                      <div className="absolute right-0 mt-2 w-72 sm:w-80 bg-white rounded-2xl shadow-2xl border border-slate-200 p-4 z-50 animate-in fade-in zoom-in-95 duration-150">
                        <div className="flex items-center justify-between pb-3 mb-3 border-b border-slate-100">
                          <span className="text-xs font-bold text-slate-800">Select Date Range</span>
                          <button
                            type="button"
                            onClick={() => setIsCalendarOpen(false)}
                            className="text-slate-400 hover:text-slate-600 p-1 rounded-lg"
                          >
                            <X size={14} />
                          </button>
                        </div>

                        {/* Date Inputs */}
                        <div className="space-y-2.5">
                          <div>
                            <label className="block text-[11px] font-semibold text-slate-600 mb-1">Start Date</label>
                            <input
                              type="date"
                              value={tempDateRange.startDate}
                              onChange={(e) => setTempDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                              className="w-full px-3 py-1.5 border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] font-semibold text-slate-600 mb-1">End Date</label>
                            <input
                              type="date"
                              value={tempDateRange.endDate}
                              min={tempDateRange.startDate}
                              onChange={(e) => setTempDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                              className="w-full px-3 py-1.5 border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
                            />
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-slate-100">
                          <button
                            type="button"
                            onClick={() => setIsCalendarOpen(false)}
                            className="px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={handleApplyCustomDates}
                            disabled={!tempDateRange.startDate || !tempDateRange.endDate}
                            className="px-3.5 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-xl shadow-sm transition-colors"
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Direct 2-Column Cards Grid with Skeleton Loader */}
            {isAnalyticsLoading ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-3 animate-in fade-in duration-200">
                {/* Left Skeleton Card: Project Distribution */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 sm:p-4 flex flex-col justify-between">
                  <div className="flex items-center justify-between pb-3 mb-2 border-b border-slate-100">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-xl bg-slate-200 animate-pulse flex-shrink-0" />
                      <div className="h-4 w-36 bg-slate-200 rounded-md animate-pulse" />
                    </div>
                  </div>

                  <div className="flex-1 flex flex-row items-center justify-between min-h-[240px] px-2">
                    {/* Doughnut Skeleton */}
                    <div className="w-[45%] flex items-center justify-center">
                      <div className="w-32 h-32 sm:w-36 sm:h-36 rounded-full border-[14px] border-slate-200 animate-pulse flex flex-col items-center justify-center">
                        <div className="h-4 w-12 bg-slate-200 rounded animate-pulse mb-1" />
                        <div className="h-2.5 w-8 bg-slate-200 rounded animate-pulse" />
                      </div>
                    </div>
                    {/* Legend Skeleton */}
                    <div className="w-[55%] pl-3 flex flex-col gap-3">
                      {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="flex items-center justify-between">
                          <div className="flex items-center gap-2 flex-1">
                            <div className="w-2.5 h-2.5 rounded-full bg-slate-200 animate-pulse shrink-0" />
                            <div className={`h-3 bg-slate-200 rounded animate-pulse ${i % 2 === 0 ? 'w-24' : 'w-20'}`} />
                          </div>
                          <div className="h-3 w-10 bg-slate-200 rounded animate-pulse shrink-0" />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right Skeleton Card: Top Task Types */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 sm:p-4 flex flex-col justify-between">
                  <div className="flex items-center justify-between pb-3 mb-2 border-b border-slate-100">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-xl bg-slate-200 animate-pulse flex-shrink-0" />
                      <div className="h-4 w-32 bg-slate-200 rounded-md animate-pulse" />
                    </div>
                  </div>

                  <div className="flex-1 flex flex-col justify-center min-h-[240px] gap-3.5 px-2">
                    {[
                      { labelW: 'w-20', barW: 'w-[85%]' },
                      { labelW: 'w-24', barW: 'w-[68%]' },
                      { labelW: 'w-16', barW: 'w-[52%]' },
                      { labelW: 'w-28', barW: 'w-[38%]' },
                      { labelW: 'w-20', barW: 'w-[22%]' },
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <div className={`h-3 ${item.labelW} bg-slate-200 rounded animate-pulse shrink-0`} />
                        <div className="flex-1 h-3.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full ${item.barW} bg-slate-200 rounded-full animate-pulse`} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-3">
                {/* Left Card: Project Distribution */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 sm:p-4 flex flex-col justify-between">
                  <div className="flex items-center justify-between pb-3 mb-2 border-b border-slate-100">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-xl bg-emerald-50 flex items-center justify-center border border-emerald-100 shadow-sm flex-shrink-0">
                        <PieChart className="h-4 w-4 text-emerald-600" />
                      </div>
                      <h3 className="text-sm sm:text-base font-bold text-slate-800">Project Distribution</h3>
                    </div>
                    {exceptionProjectDistribution.length > 7 && (
                      <button
                        type="button"
                        onClick={() => setAllProjectsModalOpen(true)}
                        className="text-[11px] font-semibold text-blue-700 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-2.5 py-1 rounded-lg border border-blue-200 transition-colors shadow-sm"
                      >
                        View All Projects
                      </button>
                    )}
                  </div>

                  <div id="chart-container" className="flex-1 flex flex-col min-h-[260px] gap-2 justify-center">
                    {exceptionPieData?.totalHours > 0 ? (
                      <div className="flex flex-row items-center justify-between flex-1 min-h-0">
                        <section id="worked-by-project" className="w-[40%] flex items-center justify-center min-h-[150px] h-full relative isolate">
                          {exceptionPieData?.chartData && (
                            <>
                              <div className="relative z-10 w-full h-[80%] flex items-center justify-center">
                                <Doughnut options={exceptionPieOptions} data={exceptionPieData?.chartData} />
                              </div>
                              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none mt-2 -z-10">
                                <span className="text-2xl sm:text-3xl font-bold text-slate-800 leading-none">
                                  {exceptionPieData?.totalHours?.toLocaleString()}h
                                </span>
                                <span className="text-xs text-slate-500 mt-1 font-medium">Total Hours</span>
                              </div>
                            </>
                          )}
                        </section>

                        {exceptionPieData?.legendData && exceptionPieData.legendData.length > 0 && (
                          <div className="w-[60%] pl-2 sm:pl-3 flex flex-col gap-2 overflow-y-auto max-h-[220px] custom-scrollbar">
                            {exceptionPieData.legendData.map((item, idx) => (
                              <div key={idx} className="flex items-center justify-between">
                                <div className="flex items-center gap-2 min-w-0 pr-2">
                                  <span className="w-2.5 h-2.5 rounded-full shadow-sm shrink-0" style={{ backgroundColor: item.color }}></span>
                                  <span className="text-[11px] sm:text-xs text-slate-600 font-medium truncate" title={item.name}>{item.name}</span>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                  <span className="text-[11px] sm:text-xs font-bold text-slate-800">{item.value?.toLocaleString()}h</span>
                                  <span className="text-[10px] text-slate-400">({item.percentage}%)</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex-1 flex flex-col items-center justify-center p-4 text-center">
                        <div className="w-12 h-12 rounded-full bg-slate-50 flex items-center justify-center mb-3">
                          <PieChart className="h-6 w-6 text-slate-400" />
                        </div>
                        <div>
                          <p className="text-sm font-bold text-slate-700">No data available</p>
                          <p className="text-xs font-medium text-slate-500 mt-0.5">No project hours logged for this period.</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Right Card: Top Task Types */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 sm:p-4 flex flex-col justify-between">
                  <div className="flex items-center justify-between pb-3 mb-2 border-b border-slate-100">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-xl bg-blue-50 flex items-center justify-center border border-blue-100 shadow-sm flex-shrink-0">
                        <BarChart2 className="h-4 w-4 text-blue-600" />
                      </div>
                      <h3 className="text-sm sm:text-base font-bold text-slate-800">Top Task Types</h3>
                    </div>
                  </div>

                  <div id="chart-container" className="flex-1 min-h-[260px] flex flex-col justify-center">
                    <section className="w-full flex-1 relative min-h-[250px]">
                      {exceptionBarChartData ? (
                        <Bar options={exceptionBarOptions} data={exceptionBarChartData} />
                      ) : (
                        <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center">
                          <div className="w-12 h-12 rounded-full bg-slate-50 flex items-center justify-center mb-3">
                            <BarChart2 className="h-6 w-6 text-slate-400" />
                          </div>
                          <div>
                            <p className="text-sm font-bold text-slate-700">No data available</p>
                            <p className="text-xs font-medium text-slate-500 mt-0.5">No task type hours logged for this period.</p>
                          </div>
                        </div>
                      )}
                    </section>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Regular Employee Charts: Current Year Hours & 90-Day Donut */
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-2 sm:gap-3 mt-1 sm:mt-1">
            
            {/* Hours Worked Chart */}
            <div className="lg:col-span-3 bg-white rounded-2xl border border-slate-200 shadow-sm p-3 sm:p-4 flex flex-col">
              <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2 mb-3 pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-xl bg-blue-50 flex items-center justify-center border border-blue-100 shadow-sm flex-shrink-0">
                    <BarChart2 className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-sm sm:text-base font-bold text-slate-800">Current Year Project Hours</h3>
                    <p className="text-[10px] sm:text-xs font-medium text-slate-500 mt-0.5">Breakdown of time spent across projects</p>
                  </div>
                </div>
                <div className="bg-blue-50 text-blue-700 px-2.5 py-1 rounded-lg text-[10px] font-bold border border-blue-200 self-start sm:self-auto flex-shrink-0">
                  {dashboardData?.barDateRange || "Current Year"}
                </div>
              </div>
                <div id="chart-container" className="flex-1 min-h-[200px] flex flex-col">
                  <section id="hours-worked" className="w-full flex-1 relative min-h-[190px]">
                    {totalProjectHours > 0 ? (
                      barChartData && <Bar options={barOptions} data={barChartData} />
                    ) : (
                      <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center">
                        <div className="w-12 h-12 rounded-full bg-slate-50 flex items-center justify-center mb-3">
                           <BarChart2 className="h-6 w-6 text-slate-400" />
                        </div>
                        <div>
                          <p className="text-sm font-bold text-slate-700">No data available</p>
                          <p className="text-xs font-medium text-slate-500 mt-0.5">There are no project hours logged for this period.</p>
                        </div>
                      </div>
                    )}
                  </section>
                </div>
            </div>

            {/* Project Allocation Chart */}
            <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm p-3 sm:p-4 flex flex-col justify-between">
              <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2 mb-3 pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-xl bg-emerald-50 flex items-center justify-center border border-emerald-100 shadow-sm flex-shrink-0">
                    <PieChart className="h-4 w-4 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="text-sm sm:text-base font-bold text-slate-800">Project distribution</h3>
                    <p className="text-[10px] sm:text-xs font-medium text-slate-500 mt-0.5">Distribution over 90 days</p>
                  </div>
                </div>
                <div className="bg-emerald-50 text-emerald-700 px-2 py-1 rounded-lg text-[10px] font-bold border border-emerald-200 self-start sm:self-auto flex-shrink-0 text-center">
                  {dashboardData?.radialDateRange || "Last 3 Months"}
                </div>
              </div>
              <div id="chart-container" className="flex-1 flex flex-col mt-1 min-h-[260px] gap-2">
                  {donutData?.total > 0 ? (
                  <div className="flex flex-row items-center justify-between flex-1 min-h-0">
                    <section id="worked-by-project" className="w-[45%] flex items-center justify-center min-h-[200px] h-full relative isolate">
                      {donutData?.chartData && (
                        <>
                          <div className="relative z-10 w-full h-full flex items-center justify-center">
                            <Doughnut options={donutOptions} data={donutData?.chartData} />
                          </div>
                          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none mt-2 -z-10">
                            <span className="text-3xl font-bold text-slate-800 leading-none">{donutData?.total}h</span>
                            <span className="text-xs text-slate-500 mt-1">Total Hours</span>
                          </div>
                        </>
                      )}
                    </section>
                    
                    {dashboardData?.projectLegendData && dashboardData.projectLegendData.length > 0 && (
                      <div className="w-[55%] pl-2 sm:pl-2 flex flex-col gap-2.5 overflow-y-auto max-h-[160px] custom-scrollbar">
                        {dashboardData.projectLegendData.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between">
                            <div className="flex items-center gap-2.5 min-w-0 pr-4">
                              <span className="w-3 h-3 rounded-full shadow-sm shrink-0" style={{backgroundColor: item.color}}></span>
                              <span className="text-[11px] text-slate-600 font-medium truncate" title={item.name}>{item.name}</span>
                            </div>
                            <span className="text-[11px] font-bold text-slate-700 shrink-0">{item.value}h</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  ) : (
                    <div className="flex-1 flex flex-col items-center justify-center p-4 text-center">
                        <div className="w-12 h-12 rounded-full bg-slate-50 flex items-center justify-center mb-3">
                           <PieChart className="h-6 w-6 text-slate-400" />
                        </div>
                        <div>
                          <p className="text-sm font-bold text-slate-700">No data available</p>
                          <p className="text-xs font-medium text-slate-500 mt-0.5">No project distribution data to show.</p>
                        </div>
                    </div>
                  )}
                </div>
            </div>
            
          </div>
        )}
      </div>

      {/* All Projects Modal for DSR Exception */}
      <AllProjectsModal
        isOpen={isAllProjectsModalOpen}
        onClose={() => setAllProjectsModalOpen(false)}
        projectsList={exceptionProjectDistribution}
        totalHours={exceptionPieData?.totalHours}
      />

      <div className="mt-0 px-4 sm:px-6 pb-2 w-full max-w-[1400px] mx-auto flex justify-center flex-shrink-0 relative z-20">

      </div>
    </div>
  );
};
